import { UseMutationResult, UseMutationOptions, useMutation } from 'react-query';
import { useDispatch } from 'react-redux';

import axios, { AxiosError } from 'axios';
import { useEffectOnce } from 'usehooks-ts';

import { loginSuccess } from '../../store/slices/user';
import axiosInstance from '../scripts/axios';

export type Variables = {
  fullname: string;
  email: string;
};

export type Payload = {
  playerId: string;
  existing: boolean;
  sessionId: string;
} & Variables;

export type Success = Omit<Payload, 'existing'>;

type UseSigninMutationProps = {
  options?: UseMutationOptions<Payload, AxiosError<Error>, Variables>;
};

export default function useSigninMutation({
  options
}: UseSigninMutationProps): UseMutationResult<Payload, AxiosError<Error>, Variables> {
  const source = axios.CancelToken.source();

  const dispatch = useDispatch();

  const mutation = useMutation(
    async (variables) => {
      const { data } = await axiosInstance.post<Payload>(
        '/login',
        { ...variables },
        { cancelToken: source.token }
      );

      return data;
    },
    {
      ...options,
      mutationKey: 'signin_mutation',
      onSuccess: (data, variables, context) => {
        localStorage.setItem('user_fullname', variables.fullname);
        localStorage.setItem('user_email', variables.email);

        dispatch(
          loginSuccess({
            sessionId: data.sessionId,
            playerId: data.playerId,
            ...variables
          })
        );

        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        console.error(error);

        if (options?.onError) {
          options.onError(error, variables, context);
        }
      }
    }
  );

  useEffectOnce(() => {
    return () => source.cancel();
  });

  return mutation;
}
